import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Button,
    FormControl,
    FormLabel,
    Input,
    Text,
    useToast,
} from '@chakra-ui/react';
import { useRef, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDeleteInventoryItem } from '../../hooks/inventory/useDeleteInventoryItem';
import { InventoryItemEntry } from '../../types/inventory';
import { formatPartNumber } from '../../functions/formatPartNumber';
import { getPrimaryPartNumber } from '../../utils/inventoryPartNumbers';

interface DeleteInventoryItemModalProps {
    isOpen: boolean;
    onClose: () => void;
    item: InventoryItemEntry | null;
    onDeleted?: () => void;
}

const DeleteInventoryItemModal = ({
    isOpen,
    onClose,
    item,
    onDeleted,
}: DeleteInventoryItemModalProps) => {
    const cancelRef = useRef<HTMLButtonElement>(null);
    const [confirmText, setConfirmText] = useState('');
    const toast = useToast();
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!isOpen) {
            setConfirmText('');
        }
    }, [isOpen]);

    const deleteMutation = useDeleteInventoryItem({
        onSuccess: () => {
            toast({
                title: 'Item deleted',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            queryClient.invalidateQueries({ queryKey: ['/inventory/feed'] });
            queryClient.invalidateQueries({ queryKey: ['/inventory/summary'] });
            queryClient.invalidateQueries({ queryKey: ['/inventory/activity-logs'] });
            onClose();
            onDeleted?.();
        },
        onError: (error: any) => {
            toast({
                title: 'Failed to delete item',
                description: error?.response?.data?.message ?? 'Please try again.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        },
    });

    const primaryPartNumber = item ? getPrimaryPartNumber(item) : '';

    const canDelete =
        !!item &&
        formatPartNumber(confirmText) === primaryPartNumber &&
        primaryPartNumber !== '' &&
        !deleteMutation.isPending;

    const handleDelete = () => {
        if (!item || !canDelete) return;
        deleteMutation.mutate({
            inventoryItemId: item._id,
            confirmManufacturerPartNumber: primaryPartNumber,
        });
    };

    return (
        <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={onClose}
            isCentered
        >
            <AlertDialogOverlay>
                <AlertDialogContent>
                    <AlertDialogHeader fontSize="md">Delete Inventory Item</AlertDialogHeader>
                    <AlertDialogBody>
                        <Text fontSize="sm" color="gray.600" mb={4}>
                            This permanently removes the item from inventory. This action cannot
                            be undone.
                        </Text>
                        {item && (
                            <Text fontSize="sm" fontWeight="medium" color="gray.800" mb={3}>
                                {item.name} ({primaryPartNumber})
                            </Text>
                        )}
                        <FormControl>
                            <FormLabel fontSize="sm">
                                Type the Mfg. Part Number to confirm
                            </FormLabel>
                            <Input
                                size="sm"
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && canDelete) {
                                        e.preventDefault();
                                        handleDelete();
                                    }
                                }}
                                placeholder={primaryPartNumber}
                                autoFocus
                            />
                        </FormControl>
                    </AlertDialogBody>
                    <AlertDialogFooter>
                        <Button size="action" variant="outlineGray" ref={cancelRef} onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            size="action"
                            colorScheme="red"
                            onClick={handleDelete}
                            ml={3}
                            isDisabled={!canDelete}
                            isLoading={deleteMutation.isPending}
                            loadingText="Deleting…"
                        >
                            Delete
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialogOverlay>
        </AlertDialog>
    );
};

export default DeleteInventoryItemModal;
